import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { PaymentBadge } from "../lib/paymentMethods";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Receipt, FileText, X, Pencil, PlusCircle } from "lucide-react";
import { useInvoiceUi } from "../hooks/useInvoiceUi";
import { gridStyle } from "../lib/invoiceUi";

const formatCurrency = (amount) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(amount || 0);
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "-");
const STATUS = { pending: "En attente", ordered: "Commandé", received: "Réceptionné", cancelled: "Annulé", invoiced: "Facturé" };
const STATUS_CLASS = { pending: "bg-yellow-100 text-yellow-700", ordered: "bg-blue-100 text-blue-700", received: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };

const PurchaseDocumentPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { settings, logoUrl } = useSettings();
  const { hasModule } = useAuth();
  const { ui } = useInvoiceUi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState("a4");

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setData(r.data)).catch(() => toast.error("Achat introuvable")).finally(() => setLoading(false));
  }, [orderId]);

  const handlePrint = (mode) => { setPrintMode(mode); setTimeout(() => window.print(), 100); };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="spinner"></div></div>;
  if (!data) return <div className="flex flex-col items-center justify-center h-screen gap-3"><p className="text-slate-500">Document indisponible</p><Button variant="outline" onClick={() => navigate("/orders")}>Fermer</Button></div>;

  const { order, supplier, invoice } = data;
  const grid = ui.format.grid !== false && ui.format.gridPrint !== false;
  const gridVars = gridStyle(ui.format);
  const box = grid ? "border inv-cell" : "border border-blue-200";
  const title = order.is_invoiced ? "FACTURE D'ACHAT" : order.status === "received" ? "BON DE RÉCEPTION" : "BON DE COMMANDE";
  const paid = invoice ? invoice.amount_paid || 0 : order.amount_paid || 0;
  const balance = invoice ? invoice.balance_due || 0 : Math.max(0, order.total - paid);
  const editable = ["pending", "ordered"].includes(order.status) && !order.is_invoiced && hasModule("purchases");
  const contactLine = [settings.address, settings.phone && `Tél: ${settings.phone}`, settings.email].filter(Boolean).join(" — ");
  const legalLine = [settings.ninea && `NINEA: ${settings.ninea}`, settings.rc && `RC: ${settings.rc}`].filter(Boolean).join(" — ");

  const CompanyHeader = ({ size = "sm" }) => (
    <div className="flex items-center gap-3">
      <img src={logoUrl} alt={settings.name} className={`${size === "lg" ? "w-14 h-14" : "w-10 h-10"} rounded-sm object-cover`} />
      <div>
        <h1 className={`${size === "lg" ? "text-xl" : "text-base"} font-bold font-[Chivo] text-slate-900 leading-tight`}>{settings.name}</h1>
        {settings.slogan && <p className="text-xs text-slate-500">{settings.slogan}</p>}
        {contactLine && <p className="text-[10px] text-slate-500">{contactLine}</p>}
        {legalLine && <p className="text-[10px] text-slate-500">{legalLine}</p>}
      </div>
    </div>
  );

  const DocContent = ({ copyType, size = "sm" }) => {
    const lg = size === "lg";
    const txt = lg ? "text-sm" : "text-xs";
    return (
      <div className="invoice-copy flex flex-col" style={grid ? gridVars : undefined} data-testid={lg ? "purchase-doc-preview" : undefined}>
        <div className="flex items-center justify-between">
          <CompanyHeader size={size} />
          <div className="text-right">
            <p className={`${lg ? "text-2xl" : "text-lg"} font-bold font-[Chivo] text-slate-900`} data-testid={lg ? "purchase-doc-title" : undefined}>{title}</p>
            <p className={`font-mono ${lg ? "text-lg" : "text-sm"} text-slate-600`}>{order.order_number}</p>
            {invoice && <p className="font-mono text-xs text-slate-500">Facture {invoice.invoice_number}</p>}
            {copyType && <p className="text-xs text-slate-400 mt-1">Copie : {copyType}</p>}
            {order.edited_at && <p className="text-[10px] text-slate-400 mt-1">Modifié le {new Date(order.edited_at).toLocaleDateString("fr-FR")} par {order.edited_by}</p>}
            {lg && <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full ${STATUS_CLASS[order.status] || "bg-slate-100 text-slate-700"}`}>{STATUS[order.status] || order.status}</span>}
          </div>
        </div>

        <div className={`grid grid-cols-3 gap-3 ${lg ? "mb-6 mt-6" : "mb-3 mt-3"} ${txt}`} data-testid={lg ? "purchase-doc-info" : undefined}>
          <div className={`${box} rounded-sm p-2`}>
            <p className="overline mb-1">Fournisseur</p>
            <p className={`font-bold text-slate-900 ${lg ? "text-base" : ""}`} data-testid={lg ? "purchase-doc-supplier" : undefined}>{order.supplier_name}</p>
            {supplier?.phone && <p className="text-slate-600">Tél: {supplier.phone}</p>}
            {supplier?.address && <p className="text-slate-600">{supplier.address}</p>}
          </div>
          <div className={`${box} rounded-sm p-2`}>
            <p className="overline mb-1">Date</p>
            <p className="font-medium text-slate-900" data-testid={lg ? "purchase-doc-date" : undefined}>{formatDate(order.order_date || order.created_at)}</p>
            {order.supplier_ref && <p className="text-xs text-slate-600 mt-1">Réf. fournisseur : <span className="font-mono font-medium">{order.supplier_ref}</span></p>}
            {order.received_at && <p className="text-xs text-slate-600">Réceptionné le {formatDate(order.received_at)}</p>}
          </div>
          <div className={`${box} rounded-sm p-2`}>
            <p className="overline mb-1">Établie par</p>
            <p className="font-medium text-slate-900">{order.created_by || "—"}</p>
            {order.depot_name && <p className="text-xs text-slate-600 mt-1">Dépôt : {order.depot_name}</p>}
          </div>
        </div>

        <table className={`invoice-table w-full ${lg ? "mb-4" : "mb-2"} ${txt} leading-tight ${grid ? "inv-grid-table" : ""}`} data-testid={lg ? "purchase-doc-table" : undefined}>
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left py-1 px-2">Article</th>
              <th className="text-center py-1 px-2 w-16">Unité</th>
              <th className="text-center py-1 px-2 w-16">{lg ? "Quantité" : "Qté"}</th>
              <th className="text-right py-1 px-2">{lg ? "Prix d'achat" : "P.A."}</th>
              <th className="text-right py-1 px-2">{lg ? "Prix total" : "P.T."}</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it, i) => (
              <tr key={i} className={grid ? "" : "border-b border-slate-100"}>
                <td className="py-0.5 px-2">{it.product_name}</td>
                <td className="text-center py-0.5 px-2 text-slate-600">{it.unit || "—"}</td>
                <td className="text-center py-0.5 px-2 font-medium">{it.quantity}</td>
                <td className="text-right py-0.5 px-2">{formatCurrency(it.unit_cost)}</td>
                <td className="text-right py-0.5 px-2">{formatCurrency(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className={`${lg ? "w-64 space-y-2" : "w-48 space-y-1"} ${txt}`}>
            <div className="flex justify-between py-1 border-b border-blue-100"><span className="text-slate-500">Articles</span><span className="font-mono">{order.items.length}</span></div>
            <div className={`flex justify-between ${lg ? "py-2 px-3" : "py-1.5 px-2"} bg-slate-900 text-white rounded-sm mt-2`}>
              <span className="font-bold">TOTAL</span>
              <span className={`font-mono font-bold ${lg ? "text-lg" : ""}`} data-testid={lg ? "purchase-doc-total" : undefined}>{formatCurrency(order.total)}</span>
            </div>
          </div>
        </div>

        <div className={`mt-auto ${lg ? "pt-6 mt-8" : "pt-3 mt-4"} border-t-2 border-slate-900`} data-testid={lg ? "purchase-doc-settlement" : undefined}>
          <p className="overline mb-2">Règlement</p>
          <div className={`grid grid-cols-3 gap-3 ${txt}`}>
            <div className={`${box} rounded-sm p-2`}>
              <p className="text-slate-500 mb-1">Mode de règlement</p>
              {paid > 0 ? <PaymentBadge method={invoice?.payment_method || order.payment_method} className={lg ? "text-sm px-3" : ""} /> : <span className="text-slate-700">{invoice ? "Facture fournisseur" : "À crédit"}</span>}
            </div>
            <div className={`${box} rounded-sm p-2`}>
              <p className="text-slate-500 mb-1">Montant payé</p>
              <p className="font-mono font-medium text-green-700" data-testid={lg ? "purchase-doc-paid" : undefined}>{formatCurrency(paid)}</p>
            </div>
            <div className={`border rounded-sm p-2 ${balance > 0 ? "border-orange-300 bg-orange-50" : box}`}>
              <p className="text-slate-500 mb-1">Reste à payer</p>
              <p className={`font-mono font-bold ${balance > 0 ? "text-orange-600" : "text-slate-900"}`} data-testid={lg ? "purchase-doc-balance" : undefined}>{formatCurrency(balance)}</p>
            </div>
          </div>
        </div>

        {order.notes && <p className={`${txt} text-slate-600 mt-3`}><span className="font-medium">Notes :</span> {order.notes}</p>}

        <div className={`grid grid-cols-2 gap-8 ${lg ? "mt-8" : "mt-5"} ${txt}`}>
          <div className="border-t border-slate-400 pt-1"><p className="text-slate-500">Signature fournisseur</p></div>
          <div className="border-t border-slate-400 pt-1 text-right"><p className="text-slate-500">Signature {settings.name}</p></div>
        </div>

        <div className={`${lg ? "mt-6 pt-4 text-sm" : "mt-3 pt-2 text-xs"} border-t border-slate-200 text-center text-slate-500`}>
          <p>{settings.name}{lg && settings.slogan ? ` - ${settings.slogan}` : ""}</p>
          <p className="mt-1 text-[10px]">Ce logiciel est créé par Amdy SECK — Copyright © IBM — 77 571 93 98</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 lg:p-8" data-testid="purchase-document-page">
      <div className="max-w-4xl mx-auto mb-4 flex items-center justify-between no-print flex-wrap gap-2">
        <Button variant="outline" className="rounded-sm" onClick={() => navigate("/orders")} data-testid="purchase-doc-close-btn"><X className="w-4 h-4 mr-2" /> Fermer</Button>
        <div className="flex gap-2 flex-wrap">
          {editable && (
            <Link to={`/purchase-entry?edit=${order.id}`}>
              <Button variant="outline" className="rounded-sm text-blue-700 border-blue-300" data-testid="edit-purchase-btn"><Pencil className="w-4 h-4 mr-2" /> Modifier</Button>
            </Link>
          )}
          <Link to="/purchase-entry"><Button variant="outline" className="rounded-sm" data-testid="new-purchase-btn"><PlusCircle className="w-4 h-4 mr-2" /> Nouvel achat</Button></Link>
          <Button variant="outline" className="rounded-sm" onClick={() => handlePrint("ticket")} data-testid="print-ticket-btn"><Receipt className="w-4 h-4 mr-2" /> Ticket</Button>
          <Button onClick={() => handlePrint("a4")} className="btn-primary" data-testid="print-purchase-doc-btn"><FileText className="w-4 h-4 mr-2" /> A4 (2 copies)</Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto no-print">
        <div className="invoice bg-white shadow-lg rounded-sm p-8"><DocContent size="lg" /></div>
      </div>

      <div className={`print-only ${printMode === "ticket" ? "print-ticket" : "hidden-print"}`}>
        <div className="ticket-invoice"><DocContent /></div>
      </div>
      <div className={`print-only ${printMode === "a4" ? "print-a4-landscape" : "hidden-print"}`}>
        <div className="a4-container">
          <div className="a4-half"><DocContent copyType="Fournisseur" /></div>
          <div className="a4-divider"></div>
          <div className="a4-half"><DocContent copyType="Comptabilité" /></div>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .hidden-print { display: none !important; }
          .print-ticket { visibility: visible; position: fixed; left: 0; top: 0; width: 80mm; }
          .print-ticket * { visibility: visible; }
          .ticket-invoice { width: 80mm; padding: 5mm; font-size: 10pt; }
          .print-a4-landscape { visibility: visible; position: fixed; left: 0; top: 0; width: 297mm; height: 210mm; }
          .print-a4-landscape * { visibility: visible; }
          .a4-container { display: flex; width: 297mm; height: 210mm; padding: 10mm; box-sizing: border-box; }
          .a4-half { flex: 1; padding: 5mm; box-sizing: border-box; }
          .a4-divider { width: 1px; background: #e2e8f0; margin: 0 5mm; }
          .invoice-copy { height: 100%; display: flex; flex-direction: column; }
          .invoice-table thead tr, .invoice-copy .bg-slate-100 { background: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: ${printMode === "a4" ? "A4 landscape" : "80mm auto"}; margin: ${printMode === "a4" ? "0" : "0 auto"}; }
        }
        .print-only { display: none; }
      `}</style>
    </div>
  );
};

export default PurchaseDocumentPage;
